import { 
  collection, 
  getDocs, 
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';
import { TransactionLogger } from './transactionLogger';

export class ResetUtils {
  /**
   * Master Reset - Deletes everything except Transaction Logs
   */
  static async masterReset(userId, userEmail) {
    try {
      console.log('Starting Master Reset...');
      
      const collections = [
        'players', 
        'tournaments', 
        'matches', 
        'scores', 
        'golfCourses',
        'restorePoints'
      ];
      
      let totalDeleted = 0;
      const deletedRecords = {};
      
      for (const collectionName of collections) {
        console.log(`Deleting all records from ${collectionName}...`);
        const snapshot = await getDocs(collection(db, collectionName));
        const batch = writeBatch(db);
        
        snapshot.docs.forEach(docSnapshot => {
          batch.delete(docSnapshot.ref);
        });
        
        if (snapshot.docs.length > 0) {
          await batch.commit();
          deletedRecords[collectionName] = snapshot.docs.length;
          totalDeleted += snapshot.docs.length;
          console.log(`Deleted ${snapshot.docs.length} records from ${collectionName}`);
        }
      }
      
      // Log the master reset transaction
      await TransactionLogger.logTransaction(
        'MASTER_RESET',
        'SYSTEM',
        'ALL',
        { deletedRecords, totalDeleted },
        null,
        userId,
        userEmail
      );
      
      console.log(`Master Reset completed. Deleted ${totalDeleted} records total.`);
      return { success: true, totalDeleted, deletedRecords };
      
    } catch (error) {
      console.error('Error during Master Reset:', error);
      throw error;
    }
  }

  /**
   * Player Reset - Deletes all players and related data
   */
  static async playerReset(userId, userEmail) {
    try {
      console.log('Starting Player Reset...');
      
      // Get all players first
      const playersSnapshot = await getDocs(collection(db, 'players'));
      const players = playersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      if (players.length === 0) {
        return { success: true, message: 'No players found to delete' };
      }
      
      // Delete all players
      const batch = writeBatch(db);
      playersSnapshot.docs.forEach(docSnapshot => {
        batch.delete(docSnapshot.ref);
      });
      await batch.commit();
      
      // Log transaction for each deleted player
      const logPromises = players.map(player => 
        TransactionLogger.logTransaction(
          'DELETE',
          'PLAYER',
          player.id,
          player,
          null,
          userId,
          userEmail
        )
      );
      await Promise.all(logPromises);
      
      console.log(`Player Reset completed. Deleted ${players.length} players.`);
      return { success: true, deletedCount: players.length };
      
    } catch (error) {
      console.error('Error during Player Reset:', error);
      throw error;
    }
  }

  /**
   * Tournament Reset - Deletes all tournaments and related data
   */
  static async tournamentReset(userId, userEmail) {
    try {
      console.log('Starting Tournament Reset...');
      
      // Get all tournaments first
      const tournamentsSnapshot = await getDocs(collection(db, 'tournaments'));
      const tournaments = tournamentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      if (tournaments.length === 0) {
        return { success: true, message: 'No tournaments found to delete' };
      }
      
      // Delete all tournaments
      const batch = writeBatch(db);
      tournamentsSnapshot.docs.forEach(docSnapshot => {
        batch.delete(docSnapshot.ref);
      });
      await batch.commit();
      
      // Log transaction for each deleted tournament
      const logPromises = tournaments.map(tournament => 
        TransactionLogger.logTransaction(
          'DELETE',
          'TOURNAMENT',
          tournament.id,
          tournament,
          null,
          userId,
          userEmail
        )
      );
      await Promise.all(logPromises);
      
      console.log(`Tournament Reset completed. Deleted ${tournaments.length} tournaments.`);
      return { success: true, deletedCount: tournaments.length };
      
    } catch (error) {
      console.error('Error during Tournament Reset:', error);
      throw error;
    }
  }

  /**
   * Get counts of records in each collection for display
   */
  static async getCollectionCounts() {
    try {
      const collections = [
        'players', 
        'tournaments', 
        'matches', 
        'scores', 
        'golfCourses',
        'backups',
        'restorePoints',
        'transactionLogs'
      ];
      
      const counts = {};
      
      for (const collectionName of collections) {
        const snapshot = await getDocs(collection(db, collectionName));
        counts[collectionName] = snapshot.docs.length;
      }
      
      return counts;
    } catch (error) {
      console.error('Error getting collection counts:', error);
      throw error;
    }
  }
}

export default ResetUtils;
